import React, { useState, useRef, useEffect } from 'react';

function SearchableSelect({ 
    name, 
    value, 
    onChange, 
    options, 
    placeholder = "Seleccionar...", 
    required = false,
    label = "",
    helpText = "",
    isInvalid = false,
    menuZIndex = 1000
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const dropdownRef = useRef(null);

    // Obtener el nombre del valor seleccionado
    const getDisplayValue = () => {
        if (!value) return '';
        const selected = options.find(opt => String(opt.Codigo) === String(value));
        return selected ? `${selected.Codigo} - ${selected.Nombre}` : value;
    };

    // Filtrar opciones cuando cambia el término de búsqueda
    useEffect(() => {
        if (searchTerm === '') {
            setFilteredOptions(options);
        } else {
            const filtered = options.filter(opt => {
                const codigo = opt.Codigo ? String(opt.Codigo).toLowerCase() : '';
                const nombre = opt.Nombre ? String(opt.Nombre).toLowerCase() : '';
                const search = searchTerm.toLowerCase();
                return codigo.includes(search) || nombre.includes(search);
            });
            setFilteredOptions(filtered);
        }
    }, [searchTerm, options]);

    // Cerrar dropdown al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (codigo) => {
        onChange({ target: { name, value: codigo } });
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleClear = () => {
        onChange({ target: { name, value: '' } });
        setSearchTerm('');
    };

    return (
        <div className="form-group">
            {label && <label>{label} {required && '*'}</label>}
            <div className="position-relative" ref={dropdownRef}>
                <div className="input-group">
                    <input
                        type="text"
                        className={`form-control ${isInvalid ? 'is-invalid' : ''}`}
                        value={getDisplayValue()}
                        onClick={() => setIsOpen(!isOpen)}
                        placeholder={placeholder}
                        readOnly
                        required={required}
                        style={{ cursor: 'pointer', backgroundColor: 'white' }}
                    />
                    <div className="input-group-append">
                        {value && (
                            <button 
                                type="button" 
                                className="btn btn-outline-secondary" 
                                onClick={handleClear}
                                title="Limpiar"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        )}
                        <button 
                            type="button" 
                            className="btn btn-info" 
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
                        </button>
                    </div>
                </div>

                {isOpen && (
                    <div 
                        className="dropdown-menu show w-100" 
                        style={{ 
                            maxHeight: '300px', 
                            overflowY: 'auto',
                            position: 'absolute',
                            zIndex: menuZIndex
                        }}
                    >
                        <div className="px-2 py-2 sticky-top bg-white border-bottom">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Buscar..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                            />
                        </div>
                        {filteredOptions.length === 0 ? (
                            <div className="dropdown-item text-muted">
                                No se encontraron resultados
                            </div>
                        ) : (
                            filteredOptions.map((option, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className={`dropdown-item ${String(option.Codigo) === String(value) ? 'active' : ''}`}
                                    onClick={() => handleSelect(option.Codigo)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <strong>{option.Codigo || ''}</strong> - {option.Nombre || ''}
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>
            {helpText && <small className={`form-text ${isInvalid ? 'text-danger' : 'text-muted'}`}>{helpText}</small>}
        </div>
    );
}

export default SearchableSelect;
